import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import GlobalMenu from "@/components/GlobalMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function CoCreate() {
  const gridRef = useRef(null);
  const viewerRef = useRef(null);
  const dragRef = useRef({ active: false, moved: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0, pointers: new Map(), pinchStart: 0, pinchZoom: 1 });
  const [data, setData] = useState(null);
  const [activeVoteSku, setActiveVoteSku] = useState(null);
  const [inlineEmail, setInlineEmail] = useState("");
  const [lockedSku, setLockedSku] = useState(null);
  const [busySku, setBusySku] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [viewerZoom, setViewerZoom] = useState(1);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/co-create/current`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load co-create archive.");
    }
  };

  useEffect(() => { load(); }, []);

  const vote = async (conceptSku) => {
    const userEmail = inlineEmail.trim().toLowerCase();
    if (!userEmail.includes("@")) {
      toast.error("Enter your email to lock your vote.");
      return;
    }
    setBusySku(conceptSku);
    try {
      const r = await axios.post(`${API}/co-create/vote`, { user_email: userEmail, concept_sku: conceptSku });
      setData(r.data);
      setLockedSku(conceptSku);
      setActiveVoteSku(null);
      try { localStorage.setItem("tbh_cocreate_email", userEmail); } catch {}
      toast.success("Vote recorded — percentages updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cast vote.");
    } finally {
      setBusySku(null);
    }
  };

  const concepts = data?.concepts || [];
  const openViewer = (image, title, sku) => {
    setViewer({ image, title, sku });
    setViewerZoom(1);
    requestAnimationFrame(() => {
      if (viewerRef.current) {
        viewerRef.current.scrollLeft = 0;
        viewerRef.current.scrollTop = 0;
      }
    });
  };
  const closeViewer = () => {
    setViewer(null);
    setViewerZoom(1);
    dragRef.current.pointers.clear();
    dragRef.current.active = false;
  };
  const toggleViewerZoom = () => {
    setViewerZoom((value) => (value > 1 ? 1 : 2.6));
  };
  const pointerDistance = (pointers) => {
    const points = Array.from(pointers.values());
    if (points.length < 2) return 0;
    return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
  };
  const onViewerPointerDown = (event) => {
    const node = viewerRef.current;
    if (!node) return;
    dragRef.current.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    dragRef.current.moved = false;
    if (dragRef.current.pointers.size === 2) {
      node.setPointerCapture?.(event.pointerId);
      dragRef.current.pinchStart = pointerDistance(dragRef.current.pointers) || 1;
      dragRef.current.pinchZoom = viewerZoom;
      dragRef.current.active = false;
      return;
    }
    if (viewerZoom <= 1) return;
    node.setPointerCapture?.(event.pointerId);
    dragRef.current.active = true;
    dragRef.current.startX = event.clientX;
    dragRef.current.startY = event.clientY;
    dragRef.current.scrollLeft = node.scrollLeft;
    dragRef.current.scrollTop = node.scrollTop;
  };
  const onViewerPointerMove = (event) => {
    const node = viewerRef.current;
    if (!node || !dragRef.current.pointers.has(event.pointerId)) return;
    dragRef.current.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (dragRef.current.pointers.size >= 2) {
      const distance = pointerDistance(dragRef.current.pointers);
      const nextZoom = Math.min(3.4, Math.max(1, dragRef.current.pinchZoom * (distance / Math.max(1, dragRef.current.pinchStart))));
      setViewerZoom(nextZoom);
      dragRef.current.moved = true;
      return;
    }
    if (!dragRef.current.active || viewerZoom <= 1) return;
    const dx = event.clientX - dragRef.current.startX;
    const dy = event.clientY - dragRef.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true;
    node.scrollLeft = dragRef.current.scrollLeft - dx;
    node.scrollTop = dragRef.current.scrollTop - dy;
  };
  const onViewerPointerEnd = (event) => {
    if (viewerRef.current?.hasPointerCapture?.(event.pointerId)) {
      viewerRef.current.releasePointerCapture(event.pointerId);
    }
    dragRef.current.pointers.delete(event.pointerId);
    dragRef.current.active = false;
  };
  const scrollToGrid = () => {
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const goBack = useCallback(() => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = "/";
    }
  }, []);

  useEffect(() => {
    window.goBack = goBack;
    window.tbhGoBack = goBack;
    return () => {
      if (window.goBack === goBack) delete window.goBack;
      if (window.tbhGoBack === goBack) delete window.tbhGoBack;
    };
  }, [goBack]);

  useEffect(() => {
    if (!viewer) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") closeViewer();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflowY = "auto";
      window.removeEventListener("keydown", onKey);
    };
  }, [viewer]);

  return (
    <main data-testid="co-create-page" className="min-h-[100svh] bg-black text-[#d3d3d3] px-2 md:px-4 py-3">
      <header className="global-brand-header border-b border-[#1a1a1a] pb-3">
        <div className="flex items-center min-h-[56px] gap-3">
          <div className="flex flex-1 items-center justify-start min-w-0">
            <button
              type="button"
              onClick={goBack}
              data-testid="co-create-back-button"
              className="inline-flex items-center justify-center min-h-[44px] px-3 py-2 font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[#d3d3d3] bg-black border border-[#1a1a1a] hover:border-[#d3d3d3] transition-colors whitespace-nowrap"
              style={{ display: "inline-flex", visibility: "visible" }}
            >
              [ &lt; BACK ]
            </button>
          </div>
          <div className="flex shrink-0 items-center justify-center gap-[15px] min-w-0 px-2">
            <img
              src="/media/logo-metallic-transparent-clean.png"
              alt="THE BLACK HOUSE logo"
              data-testid="co-create-header-logo"
              className="h-[34px] w-[34px] shrink-0 object-contain"
              draggable={false}
            />
            <span
              data-testid="co-create-header-title"
              className="font-display font-bold uppercase text-[9px] sm:text-[10px] md:text-[12px] tracking-[0.22em] text-[#d3d3d3] leading-none whitespace-nowrap"
            >
              THE BLACK HOUSE
            </span>
          </div>
          <div className="flex flex-1 items-center justify-end min-w-0">
            <GlobalMenu />
          </div>
        </div>
      </header>

      <section className="border-b border-[#1a1a1a]">
        <button
          type="button"
          onClick={scrollToGrid}
          data-testid="co-create-hero-trigger"
          className="block w-full py-3 text-left bg-black text-[#d3d3d3] hover:text-white transition-colors"
        >
          <p className="font-mono-ui text-[9px] tracking-[0.35em] uppercase text-[#8a8a8a]">Monthly Design Vote</p>
          <h1
            data-testid="co-create-main-title"
            className="mt-1 font-display font-bold uppercase leading-none whitespace-nowrap overflow-hidden text-[#d3d3d3]"
            style={{ fontSize: "clamp(10px, 3.85vw, 42px)", letterSpacing: "clamp(0em, 0.42vw, 0.12em)" }}
          >
            [ CO-CREATE THE NEXT ARCHIVE ]
          </h1>
          <p className="mt-2 max-w-3xl font-mono-ui text-[10px] md:text-[11px] tracking-[0.16em] uppercase text-[#a8a8a8] leading-relaxed">
            EACH VAULT MEMBER HOLDS A SINGLE VOTE PER DROP. SHAPE THE NEXT ARCHIVE.
            <br />
            LIVE STANDINGS UPDATE INSTANTLY UPON SUCCESSFUL SUBMISSION.
          </p>
        </button>
      </section>

      <section ref={gridRef} data-testid="co-create-design-grid" className="mt-2 scroll-mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
        {concepts.length === 0 ? (
          <div className="border border-[#333333] min-h-[320px] flex items-center justify-center font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[#777]">
            No active concepts.
          </div>
        ) : concepts.map((c) => {
          const images = c.images?.length ? c.images : (c.image_url ? [c.image_url] : []);
          return (
            <article key={c.concept_sku} data-testid="co-create-design-card" className="border border-[#333333] bg-black">
              <div className="grid grid-cols-1 md:grid-cols-[1.15fr_0.85fr] gap-0">
                <div className="aspect-[4/5] bg-[#050505] border-b md:border-b-0 md:border-r border-[#1a1a1a] overflow-hidden">
                  {images[0] ? (
                    <button
                      type="button"
                      onClick={() => openViewer(images[0], c.concept_title, c.concept_sku)}
                      data-testid={`co-create-image-open-${c.concept_sku}`}
                      className="group relative block w-full h-full cursor-zoom-in overflow-hidden bg-black"
                      aria-label={`Open ${c.concept_title} image viewer`}
                    >
                      <img
                        src={images[0]}
                        alt={c.concept_title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.025]"
                        style={{ filter: "none", WebkitFilter: "none", imageRendering: "auto" }}
                        data-testid={`co-create-design-image-${c.concept_sku}`}
                      />
                    </button>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[#666]">No Image</div>
                  )}
                </div>
                <div className="p-3 flex flex-col min-h-[280px]">
                  <p className="font-mono-ui text-[9px] tracking-[0.25em] uppercase text-[#777]">{c.concept_sku}</p>
                  <h2 className="mt-2 font-display font-bold uppercase text-[#d3d3d3] text-[18px] leading-tight" style={{ letterSpacing: "0.14em" }}>{c.concept_title}</h2>
                  <p className="mt-3 font-mono-ui text-[10px] tracking-[0.12em] uppercase text-[#a8a8a8] leading-relaxed">{c.description || "Awaiting story."}</p>
                  <p className="mt-3 font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[#d3d3d3]">Estimated Value · {c.target_price ? `₹${c.target_price}` : "TBD"}</p>
                  <div className="mt-auto pt-4">
                    <div className="flex items-center justify-between font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[#a8a8a8]">
                      <span>{c.votes || 0} Votes</span><span>{c.percentage || 0}%</span>
                    </div>
                    <div className="mt-2 h-[4px] bg-[#1a1a1a]" data-testid={`co-create-progress-${c.concept_sku}`}>
                      <div className="h-full bg-[#d3d3d3]" style={{ width: `${Math.min(100, Number(c.percentage || 0))}%` }} />
                    </div>
                    {lockedSku === c.concept_sku ? (
                      <p data-testid={`co-create-vote-locked-${c.concept_sku}`} className="mt-3 w-full border border-[#d3d3d3] py-3 text-center font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[#d3d3d3]">
                        [ VOTE LOCKED ]
                      </p>
                    ) : activeVoteSku === c.concept_sku ? (
                      <form
                        data-testid={`co-create-inline-vote-form-${c.concept_sku}`}
                        className="mt-3 flex flex-col gap-2"
                        onSubmit={(e) => {
                          e.preventDefault();
                          vote(c.concept_sku);
                        }}
                      >
                        <input
                          type="email"
                          required
                          value={inlineEmail}
                          onChange={(e) => setInlineEmail(e.target.value)}
                          placeholder="ENTER YOUR EMAIL TO LOCK VOTE"
                          data-testid={`co-create-inline-email-${c.concept_sku}`}
                          className="w-full bg-transparent border border-[#333333] px-3 py-3 font-mono-ui text-[10px] tracking-[0.12em] uppercase text-[#d3d3d3] placeholder:text-[#777] outline-none focus:border-[#d3d3d3]"
                        />
                        <div className="grid grid-cols-[1fr_auto] gap-2">
                          <button
                            type="submit"
                            disabled={data?.voting_closed || busySku === c.concept_sku}
                            data-testid={`co-create-inline-submit-${c.concept_sku}`}
                            className="border border-[#333333] py-3 font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[#d3d3d3] disabled:opacity-40 hover:border-[#d3d3d3]"
                          >
                            {data?.voting_closed ? "[ VOTING CLOSED ]" : busySku === c.concept_sku ? "Locking…" : "[ LOCK VOTE ]"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setActiveVoteSku(null)}
                            data-testid={`co-create-inline-cancel-${c.concept_sku}`}
                            className="border border-[#1a1a1a] px-3 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[#8a8a8a] hover:text-[#d3d3d3]"
                          >
                            ×
                          </button>
                        </div>
                      </form>
                    ) : (
                      <button
                        type="button"
                        disabled={data?.voting_closed || busySku === c.concept_sku}
                        onClick={() => setActiveVoteSku(c.concept_sku)}
                        data-testid={`co-create-page-vote-${c.concept_sku}`}
                        className="mt-3 w-full border border-[#333333] py-3 font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[#d3d3d3] disabled:opacity-40 hover:border-[#d3d3d3]"
                      >
                        {data?.voting_closed ? "[ VOTING CLOSED ]" : busySku === c.concept_sku ? "Casting…" : "[ CAST YOUR VOTE ]"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </section>

      {viewer && (
        <div
          data-testid="co-create-lightbox"
          className="fixed inset-0 z-[12000] bg-black/95 text-[#d3d3d3]"
          onClick={closeViewer}
        >
          <div className="absolute left-0 right-0 top-0 z-20 flex items-center justify-between gap-3 border-b border-[#1a1a1a] bg-black px-3 md:px-5 py-3">
            <div className="min-w-0">
              <p data-testid="co-create-lightbox-title" className="truncate font-display font-bold uppercase text-[12px] md:text-[14px] tracking-[0.18em] text-[#d3d3d3]">
                {viewer.title}
              </p>
              <p data-testid="co-create-lightbox-zoom-state" className="mt-1 font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[#8a8a8a]">
                {viewerZoom > 1 ? `${Math.round(viewerZoom * 100)}% · Drag / scroll to inspect` : "100% · Click image to zoom"}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); toggleViewerZoom(); }}
                data-testid="co-create-lightbox-zoom-toggle"
                className="border border-[#333333] px-3 py-2 font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[#d3d3d3] hover:border-[#d3d3d3] transition-colors"
              >
                {viewerZoom > 1 ? "Fit" : "Zoom"}
              </button>
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); closeViewer(); }}
                data-testid="co-create-lightbox-close"
                className="flex h-10 w-10 items-center justify-center border border-[#333333] font-mono-ui text-[18px] text-[#d3d3d3] hover:border-[#d3d3d3] transition-colors"
                aria-label="Close image viewer"
              >
                ×
              </button>
            </div>
          </div>
          <div
            ref={viewerRef}
            data-testid="co-create-lightbox-pan-area"
            className="absolute inset-x-0 bottom-0 top-[73px] overflow-auto overscroll-contain bg-black p-3 md:p-6"
            style={{ cursor: viewerZoom > 1 ? "grab" : "zoom-in", touchAction: viewerZoom > 1 ? "none" : "pinch-zoom" }}
            onClick={(event) => event.stopPropagation()}
            onPointerDown={onViewerPointerDown}
            onPointerMove={onViewerPointerMove}
            onPointerUp={onViewerPointerEnd}
            onPointerCancel={onViewerPointerEnd}
          >
            <div className="flex min-h-full items-start justify-center">
              <button
                type="button"
                data-testid="co-create-lightbox-image-button"
                className="block border border-[#1a1a1a] bg-black p-0"
                onClick={(event) => {
                  event.stopPropagation();
                  if (!dragRef.current.moved) toggleViewerZoom();
                  dragRef.current.moved = false;
                }}
                style={{
                  width: viewerZoom > 1 ? `${Math.round(viewerZoom * 88)}vw` : "auto",
                  maxWidth: viewerZoom > 1 ? "none" : "min(92vw, 1400px)",
                  minWidth: viewerZoom > 1 ? `${Math.round(viewerZoom * 88)}vw` : "auto",
                  height: viewerZoom > 1 ? "auto" : "calc(100svh - 122px)",
                  maxHeight: viewerZoom > 1 ? "none" : "calc(100svh - 122px)",
                  flex: "0 0 auto",
                  cursor: viewerZoom > 1 ? "grab" : "zoom-in",
                }}
              >
                <img
                  src={viewer.image}
                  alt={viewer.title}
                  data-testid="co-create-lightbox-image"
                  className="block select-none bg-black"
                  draggable={false}
                  style={{
                    filter: "none",
                    WebkitFilter: "none",
                    imageRendering: "auto",
                    width: viewerZoom > 1 ? "100%" : "auto",
                    maxWidth: viewerZoom > 1 ? "none" : "min(92vw, 1400px)",
                    maxHeight: viewerZoom > 1 ? "none" : "calc(100svh - 122px)",
                    height: viewerZoom > 1 ? "auto" : "100%",
                    pointerEvents: "none",
                  }}
                />
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}